import { Button } from "@exopengithub/exokit/v2";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Card } from "../planning/components/Card.js";
import { UserIdentitySummary } from "molecules/userIdentitySummary/UserIdentitySummary";
import { DOMAIN_TERMS } from "src/common/domainTerms.js";
import { SCOPE_SWITCH_STATE } from "src/routes/scopeChange.js";

interface DomainSummary {
	id: number;
	name: string;
}

interface CompanySummary {
	id: string;
	name: string;
}

interface ProfileViewProps {
	avatarSrc: string;
	gravatarProfileUrl: string;
	name: string;
	email: string;
	superAdmin: boolean;
	roleNames: string[];
	domains: DomainSummary[];
	companies: CompanySummary[];
	companyDomainId: string | undefined;
	/** Resolved from the already-loaded domain list — undefined when nothing's remembered. */
	defaultDomainName: string | undefined;
	defaultCompanyName: string | undefined;
	onResetDefaultCompany: () => void;
}

export const ProfileView = ({
	avatarSrc,
	gravatarProfileUrl,
	name,
	email,
	superAdmin,
	roleNames,
	domains,
	companies,
	companyDomainId,
	defaultDomainName,
	defaultCompanyName,
	onResetDefaultCompany,
}: ProfileViewProps) => {
	const { t } = useTranslation();

	return (
		<Card>
			<div className="p-6">
				<UserIdentitySummary
					avatarSrc={avatarSrc}
					avatarHref={gravatarProfileUrl}
					name={name}
					email={email}
				/>
			</div>
			<div className="border-gray-200 border-t p-6">
				<div className="mb-2 font-medium text-gray-700 text-sm">
					{t("Roles")}
				</div>
				<div className="space-y-1 text-gray-500 text-sm">
					{superAdmin && <div>{t("Superadmin")}</div>}
					{roleNames.map((roleName) => (
						<div key={roleName}>{roleName}</div>
					))}
					{!superAdmin && roleNames.length === 0 && (
						<div>{t("No company access")}</div>
					)}
				</div>
			</div>
			<div className="border-gray-200 border-t p-6">
				<div className="mb-2 font-medium text-gray-700 text-sm">
					{t(DOMAIN_TERMS.DOMAINS)}
				</div>
				<div className="max-h-40 space-y-1 overflow-y-auto text-sm">
					{superAdmin && (
						<div className="text-gray-500">
							{t("Has access to all domains")}
						</div>
					)}
					{domains.map((domain) => (
						<Link
							key={domain.id}
							to={`/company-domains/${domain.id}`}
							state={SCOPE_SWITCH_STATE}
							className="block text-purple-600 hover:underline"
						>
							{domain.name}
						</Link>
					))}
					{!superAdmin && domains.length === 0 && (
						<div className="text-gray-500">{t("No company access")}</div>
					)}
				</div>
			</div>
			<div className="border-gray-200 border-t p-6">
				<div className="mb-2 font-medium text-gray-700 text-sm">
					{t(DOMAIN_TERMS.COMPANIES)}
				</div>
				<div className="max-h-40 space-y-1 overflow-y-auto text-sm">
					{companies.map((company) => (
						<Link
							key={company.id}
							to={`/company-domains/${companyDomainId}/legal-entities/${company.id}`}
							state={SCOPE_SWITCH_STATE}
							className="block text-purple-600 hover:underline"
						>
							{company.name}
						</Link>
					))}
					{companies.length === 0 && (
						<div className="text-gray-500">{t("No companies")}</div>
					)}
				</div>
				<div className="mt-4 flex items-center justify-between gap-4 border-gray-200 border-t pt-4">
					<div className="space-y-2">
						<div>
							<div className="text-gray-500">{t("Default domain")}</div>
							<div className="text-gray-900">
								{defaultDomainName ?? t("Not set")}
							</div>
						</div>
						<div>
							<div className="text-gray-500">{t("Default company")}</div>
							<div className="text-gray-900">
								{defaultCompanyName ?? t("Not set")}
							</div>
						</div>
					</div>
					<Button
						variant="ghost"
						disabled={!defaultDomainName}
						onClick={onResetDefaultCompany}
					>
						{t("Reset")}
					</Button>
				</div>
			</div>
		</Card>
	);
};
